import { getCoachGradeLabel } from '../constants/coachGrades';
import { getRegionLabel } from './tanzaniaRegions';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const isWithinLast30Days = (dateValue) => {
  if (!dateValue) return false;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;
  return Date.now() - date.getTime() <= THIRTY_DAYS_MS;
};

const isWithinNext30Days = (dateValue) => {
  if (!dateValue) return false;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;
  const diff = date.getTime() - Date.now();
  return diff >= 0 && diff <= THIRTY_DAYS_MS;
};

const countBy = (items, getKey) => {
  const map = {};
  (items || []).forEach((item) => {
    const key = getKey(item) || 'Unknown';
    map[key] = (map[key] || 0) + 1;
  });
  return map;
};

const toBreakdown = (map, labelFn = (key) => key, limit = 5) =>
  Object.entries(map)
    .map(([key, value]) => ({ label: labelFn(key), value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

export function computeOfficialAnalytics(officials = []) {
  const total = officials.length;
  const available = officials.filter((o) => o.isAvailable).length;
  const unavailable = total - available;
  const recentlyAdded = officials.filter((o) => isWithinLast30Days(o.createdAt)).length;
  const licensed = officials.filter((o) => o.licenseNumber?.trim()).length;

  const bySpecialization = countBy(officials, (o) => o.specialization);
  const referees = bySpecialization.Referee || 0;
  const tableOfficials = bySpecialization['Table Official'] || 0;
  const commissioners = bySpecialization.Commissioner || 0;

  const experienceTotal = officials.reduce((sum, o) => sum + (Number(o.experienceYears) || 0), 0);
  const avgExperience = total ? Math.round((experienceTotal / total) * 10) / 10 : 0;

  const ratedOfficials = officials.filter((o) => Number(o.rating) > 0);
  const avgRating = ratedOfficials.length
    ? Math.round(
        (ratedOfficials.reduce((sum, o) => sum + Number(o.rating), 0) / ratedOfficials.length) * 10
      ) / 10
    : 0;

  const certificationBreakdown = toBreakdown(
    countBy(officials, (o) => o.certificationLevel || 'Not specified'),
    (key) => key,
    6
  );

  return {
    stats: [
      { label: 'Total Officials', value: total, icon: 'Users', hint: 'Registered in the system' },
      {
        label: 'Available',
        value: available,
        icon: 'CheckCircle',
        hint: `${total ? Math.round((available / total) * 100) : 0}% ready for assignment`,
        tone: 'success',
      },
      {
        label: 'Unavailable',
        value: unavailable,
        icon: 'Clock',
        hint: 'Not currently available',
        tone: unavailable ? 'warning' : 'neutral',
      },
      {
        label: 'Licensed',
        value: licensed,
        icon: 'BadgeCheck',
        hint: 'With license numbers on file',
      },
      {
        label: 'New (30 days)',
        value: recentlyAdded,
        icon: 'TrendingUp',
        hint: 'Recently registered',
        tone: recentlyAdded ? 'accent' : 'neutral',
      },
      {
        label: 'Avg Experience',
        value: `${avgExperience}y`,
        icon: 'Briefcase',
        hint: avgRating ? `Avg rating ${avgRating}/5` : 'Years across all officials',
      },
    ],
    breakdown: [
      { label: 'Referees', value: referees },
      { label: 'Table Officials', value: tableOfficials },
      { label: 'Commissioners', value: commissioners },
    ],
    availabilityBreakdown: [
      { label: 'Available', value: available },
      { label: 'Unavailable', value: unavailable },
    ],
    certificationBreakdown,
  };
}

const PENDING_REGISTRATION = ['submitted', 'under_review'];

export function computeCoachAnalytics(coaches = []) {
  const total = coaches.length;
  const activeLicenses = coaches.filter((c) => c.licenseStatus === 'active').length;
  const pendingLicenses = coaches.filter((c) => c.licenseStatus === 'pending').length;
  const suspendedLicenses = coaches.filter((c) => c.licenseStatus === 'suspended').length;
  const expiredLicenses = coaches.filter((c) => c.licenseStatus === 'expired').length;
  const pendingApproval = coaches.filter((c) => PENDING_REGISTRATION.includes(c.registrationStatus)).length;
  const approved = coaches.filter((c) => c.registrationStatus === 'approved').length;
  const recentlyAdded = coaches.filter((c) => isWithinLast30Days(c.createdAt)).length;
  const withTeams = coaches.filter((c) => (c.teamAssignments || []).length > 0).length;
  const expiringSoon = coaches.filter((c) => isWithinNext30Days(c.licenseExpirationDate)).length;

  const licenseBreakdown = [
    { label: 'Active', value: activeLicenses },
    { label: 'Pending', value: pendingLicenses },
    { label: 'Suspended', value: suspendedLicenses },
    { label: 'Expired', value: expiredLicenses },
  ].filter((item) => item.value > 0);

  const gradeBreakdown = toBreakdown(
    countBy(coaches, (c) => c.grade),
    (key) => getCoachGradeLabel(key) || key,
    5
  );

  const regionBreakdown = toBreakdown(
    countBy(coaches, (c) => c.region),
    (key) => getRegionLabel(key) || key,
    5
  );

  return {
    stats: [
      { label: 'Total Coaches', value: total, icon: 'GraduationCap', hint: 'All registered coaches' },
      {
        label: 'Active Licenses',
        value: activeLicenses,
        icon: 'BadgeCheck',
        hint: `${total ? Math.round((activeLicenses / total) * 100) : 0}% of all coaches`,
        tone: 'success',
      },
      {
        label: 'Pending Approval',
        value: pendingApproval,
        icon: 'Clock',
        hint: 'Awaiting review',
        tone: pendingApproval ? 'warning' : 'neutral',
      },
      {
        label: 'With Teams',
        value: withTeams,
        icon: 'Users',
        hint: 'Assigned to club or regional teams',
      },
      {
        label: 'Expiring Soon',
        value: expiringSoon,
        icon: 'AlertTriangle',
        hint: 'Licenses expiring within 30 days',
        tone: expiringSoon ? 'error' : 'neutral',
      },
      {
        label: 'New (30 days)',
        value: recentlyAdded,
        icon: 'TrendingUp',
        hint: `${approved} approved overall`,
        tone: recentlyAdded ? 'accent' : 'neutral',
      },
    ],
    licenseBreakdown,
    gradeBreakdown,
    regionBreakdown,
    registrationBreakdown: [
      { label: 'Approved', value: approved },
      { label: 'Pending Review', value: pendingApproval },
      {
        label: 'Other',
        value: Math.max(0, total - approved - pendingApproval),
      },
    ].filter((item) => item.value > 0),
  };
}
